import { useCallback, useContext, useState } from 'react';
import UWEEntityApi from '../../api/UWEEntityApi';
import UWEEntityUWEFormComponent from '../../blc/UWEEntityUWEFormComponent';
import Loader from '../Loader';
import { AuthContext } from '../../context/AuthContext';
import useResourceLoader from '../../util/useResourceLoader';
import useOptionsLookup, { LookupObject } from '../customFields/hooks/useOptionsLookup';

// ─── Types ────────────────────────────────────────────────────────────────────

interface WorkpageFormDef {
  schema: object;
  uiSchema?: object;
  title?: string;
  sidebarComponents?: any[];
}

interface WorkpageFormElement {
  title?: string;
  entityLookup: LookupObject;
  form: WorkpageFormDef;
}

// ─── Component ────────────────────────────────────────────────────────────────

const formContext = {};

function WorkpageForm({ element, scope }: { element: WorkpageFormElement; scope?: any }) {
  const auth = useContext(AuthContext);
  const { entityLookup, form } = element;

  const { options, loadingOptions, errorLoadingOptions } = useOptionsLookup({
    lookup: entityLookup,
    rootFormData: scope ?? {},
    path: '',
    auth,
    formContext,
  });

  const lookupEntity = options != null ? (Array.isArray(options) ? options[0] : options) : null;

  const [wfAccessToken, loadingToken = true] = useResourceLoader(async () => {
    if (!lookupEntity?.id) return null;
    return UWEEntityApi.createWfAccessTokenHash({
      uweEntityId: lookupEntity.id,
      canView: true,
      canCreate: true,
      canUpdate: true,
    });
  }, [lookupEntity?.id]);

  const [currentEntity, setCurrentEntity] = useState<any>(null);

  const targetEntityId = currentEntity?.id ?? lookupEntity?.id;
  const [fullEntity, loadingFullEntity = true, errorFullEntity] = useResourceLoader(async () => {
    if (!targetEntityId || !wfAccessToken) return;
    try {
      const result = UWEEntityApi.getUWEEntity({ entityId: targetEntityId, wfAccessToken });
      return result;
    } catch {
      console.error(`Error fetching target entity ${targetEntityId}`);
    }
  }, [targetEntityId, wfAccessToken]);

  const handleSubmit = useCallback(
    async ({ payload }) => {
      const formData = payload?.formData?.data;
      if (formData == null) return;

      if (!fullEntity?.id || !wfAccessToken) return;
      const updated = await UWEEntityApi.updateUWEEntityData({
        id: fullEntity.id,
        data: formData,
        wfAccessToken,
      });
      if (updated) setCurrentEntity(updated);
      return { data: updated };
    },
    [fullEntity?.id, wfAccessToken]
  );

  if (loadingOptions || loadingToken || loadingFullEntity) return <Loader fullscreen />;

  if (!fullEntity || !wfAccessToken) return null;

  return (
    <UWEEntityUWEFormComponent
      form={form}
      currentEntity={fullEntity}
      wfAccessToken={wfAccessToken}
      overrideOnSubmit={handleSubmit}
      setCurrentEntity={setCurrentEntity}
      hideSideTabs
    />
  );
}

export default WorkpageForm;
